import PropTypes from 'prop-types';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import I18n from 'I18n';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { PlayerPlayIcon, PlayerPauseIcon } from 'foundations-assets';
import { Slider as FoundationsSlider } from 'foundations-components/transitional/components/Slider';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const getFormattedTime = milliseconds => {
  const momentPlaybackTime = I18n.moment(0).hours(0).minutes(0).second(0).milliseconds(milliseconds);
  return momentPlaybackTime.hours() > 0 ? momentPlaybackTime.format('H:mm:ss') : momentPlaybackTime.format('m:ss');
};
export const getPercentageTimeRemaining = (currentTime, totalTime) => {
  return currentTime / totalTime * 100;
};
const NoWrap = styled.div.withConfig({
  displayName: "CallRecordingPlayer__NoWrap",
  componentId: "sc-1x8h7j9-0"
})(["white-space:nowrap;"]);
const CallRecordingPlayer = ({
  currentPlaybackTime,
  duration,
  isPaused,
  canSeek,
  isLoading,
  onPauseRecording,
  onPlayRecording,
  onSkipToTime,
  hasVoicemail
}) => {
  const LoadingMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: hasVoicemail ? 'conversations-calling-message.timeline-call-metadata.call-voicemail.loading-tooltip' : 'conversations-calling-message.timeline-call-metadata.call-recording.loading-tooltip'
  });
  const SeekingUnavailableMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: hasVoicemail ? 'conversations-calling-message.timeline-call-metadata.call-voicemail.seeking-unavailable-tooltip' : 'conversations-calling-message.timeline-call-metadata.call-recording.seeking-unavailable-tooltip'
  });
  const PlayButton = /*#__PURE__*/_jsx(IconButton, {
    "data-selenium-test": isPaused ? 'call-recording-play-button' : 'call-recording-pause-button',
    "data-test-id": isPaused ? 'call-recording-play-button' : 'call-recording-pause-button',
    disabled: isLoading,
    tooltip: isLoading && LoadingMessage,
    onClick: isPaused ? onPlayRecording : onPauseRecording,
    use: "link",
    marginInlineEnd: "100",
    children: isPaused ? /*#__PURE__*/_jsx(PlayerPlayIcon, {}) : /*#__PURE__*/_jsx(PlayerPauseIcon, {})
  });
  const CurrentPlaybackTime = /*#__PURE__*/_jsx(NoWrap, {
    children: getFormattedTime(currentPlaybackTime)
  });
  const Duration = /*#__PURE__*/_jsx(NoWrap, {
    children: getFormattedTime(duration)
  });
  const Slider = /*#__PURE__*/_jsx(Tooltip, {
    disabled: !isLoading && canSeek,
    title: isLoading ? LoadingMessage : SeekingUnavailableMessage,
    "data-test-id": "call-recording-player-slider-tooltip",
    children: /*#__PURE__*/_jsx(View, {
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(FoundationsSlider, {
        "data-test-id": "call-recording-player-slider",
        disabled: isLoading || !canSeek,
        max: 100,
        min: 0,
        step: 1,
        onChange: onSkipToTime,
        value: getPercentageTimeRemaining(currentPlaybackTime, duration),
        marginInlineEnd: "400",
        marginInlineStart: "600",
        marginBlockStart: "100"
      })
    })
  });
  return /*#__PURE__*/_jsxs(Flex, {
    "data-test-id": "call-recording-player",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    inlineSize: "100%",
    children: [PlayButton, CurrentPlaybackTime, Slider, Duration]
  });
};
CallRecordingPlayer.propTypes = {
  currentPlaybackTime: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  isPaused: PropTypes.bool.isRequired,
  onPauseRecording: PropTypes.func.isRequired,
  onPlayRecording: PropTypes.func.isRequired,
  onSkipToTime: PropTypes.func.isRequired,
  canSeek: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasVoicemail: PropTypes.bool
};
export default CallRecordingPlayer;